import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Search, Wallet } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter';
import { GmailPagination } from '@/components/ui/GmailPagination';

type Row = {
  id: number;
  createdAt: string;
  merchantId: number;
  merchantName: string;
  salesUserName?: string | null;
  txnType: string;
  amount: number;
  balanceAfter: number;
  refType?: string | null;
  refId?: string | null;
  paymentGateway?: string | null;
  remarks?: string | null;
};

const TYPE_BADGE: Record<string, string> = {
  CREDIT: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  DEBIT: 'bg-rose-50 text-rose-700 border-rose-200',
  REFUND: 'bg-sky-50 text-sky-700 border-sky-200',
  ADJUSTMENT: 'bg-amber-50 text-amber-700 border-amber-200',
  REMITTANCE_PAID: 'bg-violet-50 text-violet-700 border-violet-200',
};

const inr = (n: number) => `₹${Number(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Every merchant's wallet movements in one place.
 *
 * Previously a wallet could only be read from inside one merchant's record, so "what did we charge
 * everyone this week" meant opening accounts one at a time.
 */
export function WalletLedgerPage() {
  const [merchantIds, setMerchantIds] = useState<(string | number)[]>([]);
  const [txnTypes, setTxnTypes] = useState<(string | number)[]>([]);
  const [refTypes, setRefTypes] = useState<(string | number)[]>([]);
  const [q, setQ] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data: merchants } = useQuery({
    queryKey: ['wallet-ledger-merchants'],
    queryFn: async () => (await api.get<any[]>('/merchants', { params: { pageSize: 500 } })).data,
    staleTime: 10 * 60_000,
  });

  const params = useMemo(() => {
    const p: any = { page, pageSize };
    if (merchantIds.length) p.merchantIds = merchantIds.join(',');
    if (txnTypes.length) p.txnTypes = txnTypes.join(',');
    if (refTypes.length) p.refTypes = refTypes.join(',');
    if (q.trim()) p.q = q.trim();
    // A reference search deliberately ignores the dates — see the API. Not sending them keeps the
    // screen honest about which filters were actually applied.
    else { if (fromDate) p.fromDate = fromDate; if (toDate) p.toDate = toDate; }
    return p;
  }, [merchantIds, txnTypes, refTypes, q, fromDate, toDate, page, pageSize]);

  const { data, isLoading } = useQuery({
    queryKey: ['wallet-ledger', params],
    queryFn: async () => (await api.get('/admin/wallet-ledger', { params })).data as {
      total: number; items: Row[]; summary: { totalCredit: number; totalDebit: number; net: number; byType: Record<string, { count: number; amount: number }> };
    },
  });

  const reset = () => setPage(1);
  const rows = data?.items ?? [];
  const searching = q.trim().length > 0;

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Wallet size={22} className="text-emerald-700" /> Wallet Ledger</h1>
          <p className="text-sm text-slate-500">Every merchant's wallet movement — recharges, freight and remittances</p>
        </div>
        <button
          onClick={() => downloadBlob(`/admin/wallet-ledger/export?${new URLSearchParams(
            Object.fromEntries(Object.entries(params).filter(([k]) => k !== 'page' && k !== 'pageSize')) as any,
          ).toString()}`, `wallet-ledger-${new Date().toISOString().slice(0, 10)}.csv`)}
          className="border border-slate-300 text-slate-700 px-4 py-2 rounded-lg font-medium hover:bg-slate-50 flex items-center gap-2">
          <Download size={18} /> Export
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <Tile label="Total Credit" value={data?.summary?.totalCredit} tone="text-emerald-700" />
        <Tile label="Total Debit" value={data?.summary?.totalDebit} tone="text-rose-700" />
        <Tile label="Net" value={data?.summary?.net} tone={(data?.summary?.net ?? 0) < 0 ? 'text-rose-700' : 'text-slate-900'} />
      </div>

      <div className="grid grid-cols-12 gap-3 mb-4">
        <div className="col-span-3">
          <label className="text-xs text-slate-500">Merchant</label>
          <MultiSelectFilter label="" placeholder="All merchants" values={merchantIds}
            onChange={(v) => { setMerchantIds(v); reset(); }}
            options={(merchants ?? []).map((m: any) => ({ value: m.id, label: `#${m.id} · ${m.companyName}` }))} />
        </div>
        <div className="col-span-2">
          <label className="text-xs text-slate-500">Type</label>
          <MultiSelectFilter label="" placeholder="All types" searchable={false} values={txnTypes}
            onChange={(v) => { setTxnTypes(v); reset(); }}
            options={[
              { value: 'CREDIT', label: 'Credit (recharge)' },
              { value: 'DEBIT', label: 'Debit (freight)' },
              { value: 'REMITTANCE_PAID', label: 'Remittance paid' },
              { value: 'REFUND', label: 'Refund' },
              { value: 'ADJUSTMENT', label: 'Adjustment' },
            ]} />
        </div>
        <div className="col-span-2">
          <label className="text-xs text-slate-500">Source</label>
          <MultiSelectFilter label="" placeholder="All sources" searchable={false} values={refTypes}
            onChange={(v) => { setRefTypes(v); reset(); }}
            options={[
              { value: 'freight', label: 'Freight' },
              { value: 'recharge', label: 'Recharge' },
              { value: 'remittance', label: 'Remittance' },
            ]} />
        </div>
        <div className="col-span-2">
          <label className="text-xs text-slate-500">Search</label>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => { setQ(e.target.value); reset(); }} placeholder="AWB / reference / UTR"
              className="w-full border rounded pl-7 pr-2 py-1.5 text-sm" />
          </div>
          {searching && <div className="text-[10px] text-amber-600 mt-0.5">Searching all dates</div>}
        </div>
        <div className="col-span-1">
          <label className="text-xs text-slate-500">From</label>
          <input type="date" value={fromDate} disabled={searching} onChange={(e) => { setFromDate(e.target.value); reset(); }} className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-slate-50" />
        </div>
        <div className="col-span-1">
          <label className="text-xs text-slate-500">To</label>
          <input type="date" value={toDate} disabled={searching} onChange={(e) => { setToDate(e.target.value); reset(); }} className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-slate-50" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-emerald-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Merchant</th>
                <th className="px-3 py-2 text-left">Sale Person</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-left">Reference</th>
                <th className="px-3 py-2 text-left">Remarks</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-right">Balance After</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={9} className="text-center py-8 text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-slate-400">No wallet entries for these filters.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">
                    {new Date(r.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-slate-800">{r.merchantName}</div>
                    <div className="text-[10px] text-slate-400 font-mono">#{r.merchantId}</div>
                  </td>
                  <td className={`px-3 py-2 text-xs ${r.salesUserName ? 'text-slate-700' : 'text-slate-400 italic'}`}>{r.salesUserName ?? 'Unassigned'}</td>
                  <td className="px-3 py-2">
                    <span className={`border rounded-full px-2 py-0.5 text-[11px] ${TYPE_BADGE[r.txnType] ?? 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                      {r.txnType === 'REMITTANCE_PAID' ? 'Remittance' : r.txnType.charAt(0) + r.txnType.slice(1).toLowerCase()}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600 capitalize">{r.refType ?? '—'}</td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-600">{r.refId ?? '—'}</td>
                  <td className="px-3 py-2 text-xs text-slate-500 max-w-[280px] truncate" title={r.remarks ?? ''}>{r.remarks ?? '—'}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-medium ${r.txnType === 'DEBIT' ? 'text-rose-700' : 'text-emerald-700'}`}>
                    {r.txnType === 'DEBIT' ? '−' : '+'}{inr(r.amount)}
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums ${r.balanceAfter < 0 ? 'text-rose-700' : 'text-slate-700'}`}>{inr(r.balanceAfter)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <GmailPagination page={page} pageSize={pageSize} total={data?.total ?? 0} onPage={setPage} />
      </div>
    </div>
  );
}

function Tile({ label, value, tone }: { label: string; value?: number; tone: string }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-xl font-bold tabular-nums ${tone}`}>{inr(value ?? 0)}</div>
    </div>
  );
}
